<template>
  <div class="order-info">
    <BaseElForm
      v-if="order"
      label-width="150px"
      label-position="left"
    >
      <!-- <p class="section-title text-primary-100 font-medium">{{ index }}.詳細資訊</p> -->
      <BaseElFormItem v-if="!readonly && checkAction('admin.appointmentReservation.update')" :label="$t('common.table.action.text')">
        <BaseElButton :disabled="isCancel" class="primary-btn underline" type="text" @click="showEditDialog = true">
          {{ $t('orderDetailBlock.button.editOrderDetail.text') }}
        </BaseElButton>
      </BaseElFormItem>
      <BaseElFormItem :label="$t('orderDetailBlock.numberCode.title')">
        <div>{{ displayData.numberCode }}</div>
      </BaseElFormItem>
      <BaseElFormItem :label="$t('orderDetailBlock.totalPrice.title')">
        <div>{{ displayData.totalPrice }}</div>
      </BaseElFormItem>
      <BaseElFormItem :label="isPubApt ? $t('orderDetailBlock.visitor.name.title') : $t('orderDetailBlock.user.name.title')">
        <div>{{ displayData.userName }}</div>
      </BaseElFormItem>
      <BaseElFormItem :label="isPubApt ? $t('orderDetailBlock.visitor.phone.title') : $t('orderDetailBlock.user.phone.title')">
        <div>{{ displayData.userPhone }}</div>
      </BaseElFormItem>
      <BaseElFormItem v-if="showControl.formItem.riskLevel">
        <template #label>
          <div class="flex items-center gap-1">
            <span>{{ $t('orderDetailBlock.user.riskLevel.title') }}</span>
            <el-tooltip
              :content="displayText.riskLevel.tooltip"
              placement="right"
              :popper-class="'max-w-xs'"
            >
              <span class="material-icons text-xl">help_outline</span>
            </el-tooltip>
          </div>
        </template>
        <Tag
          v-if="displayData.riskLevel"
          style="display: inline-flex"
          :type="displayData.riskLevelDisplay.tag"
          disable-transitions
        >
          {{ displayData.riskLevelDisplay.label }}
        </Tag>
        <span v-else>-</span>
      </BaseElFormItem>
      <BaseElFormItem :label="$t('orderDetailBlock.start.title')">
        <div>{{ dateFormat(displayData.start, shopTimezone) }}</div>
      </BaseElFormItem>
      <BaseElFormItem :label="$t('orderDetailBlock.serviceName.title')">
        <div>{{ displayData.serviceName }}</div>
      </BaseElFormItem>
      <BaseElFormItem v-if="displayData.subServiceName" :label="$t('orderDetailBlock.subServiceName.title')">
        <div>{{ displayData.subServiceName }}</div>
      </BaseElFormItem>
      <BaseElFormItem :label="$t('orderDetailBlock.serviceUnit.title')">
        <div>{{ showNotSpecify(order) }}</div>
      </BaseElFormItem>
      <BaseElFormItem v-if="useServiceResource" :label="$t('orderDetailBlock.resource.title')">
        <div>{{ displayData.resourceItemName }}</div>
      </BaseElFormItem>
      <BaseElFormItem :label="$t('orderDetailBlock.attachService.title')">
        <div>{{ attachServices }}</div>
      </BaseElFormItem>
      <BaseElFormItem :label="$t('orderDetailBlock.createdAt.title')">
        <div>{{ createdAt }}</div>
      </BaseElFormItem>
      <!-- <BaseElFormItem label="店家備註">
        <div>{{ order.remark || '無' }}</div>
      </BaseElFormItem> -->
    </BaseElForm>

    <EditReservationDialog
      v-if="!readonly && showEditDialog"
      :isPubApt="isPubApt"
      :data="order"
      :usingClassTickets="syncData.classTickets"
      :disableFields="statusNotWait"
      @close="showEditDialog=false"
      @refresh="$emit('refresh')"
    />
  </div>
</template>

<script>
import { defineComponent, computed } from 'vue'
import EditReservationDialog from '@/components/Reservation/EditReservationDialog/EditReservationDialog.vue'
import { getData } from '@/utils/object'
import { map, get } from 'lodash'
import { usePermissions } from '@/use/permissions'
import { useShop } from '@/use/shop'
import { dateTimeFormat } from '@/utils/helper'
import { appointmentRiskGuardConfig } from '@/config/appointmentRiskGuard'

export default defineComponent({
  name: 'OrderDetailBlock',
  components: { EditReservationDialog },
  props: {
    order: {
      type: Object,
      default: () => ({}),
    },
    index: Number,
    syncData: { type: Object, default: () => ({}) },
    readonly: Boolean,
  },
  setup (props) {
    const { shopTimezone } = useShop()
    const { checkAction } = usePermissions()
    const isPubApt = computed(() => get(props.order, 'AppointmentOrder.origin') === 'pubApt')
    const useServiceResource = computed(() => {
      return checkAction('admin.resourceItem.page')
    })
    const statusNotWait = computed(() => {
      return getData(props.order, 'AppointmentOrder.status') !== 'wait'
    })

    const showControl = computed(() => ({
      formItem: {
        riskLevel: checkAction('planModule.appointmentRiskGuard.enable'),
      },
    }))

    // 風險等級顯示配置
    const getRiskLevelDisplay = () => {
      const riskLevel = get(props.order, 'AppointmentOrder.riskLevel')
      if (!riskLevel) {
        return appointmentRiskGuardConfig.riskLevels.null
      }
      return appointmentRiskGuardConfig.riskLevels[riskLevel] || appointmentRiskGuardConfig.riskLevels.null
    }

    const displayText = computed(() => ({
      riskLevel: {
        tooltip: '可透過快速收款連結，向中/高風險消費者請求預先付款。',
      },
    }))

    const displayData = computed(() => {
      const order = props.order
      const AppointmentVisitorPreOrder = get(order, 'AppointmentOrder.AppointmentVisitorPreOrder')
      const numberCode = get(order, 'numberCode', '')
      const totalPrice = get(order, 'totalPrice', 0)
      const userName = isPubApt.value ? get(AppointmentVisitorPreOrder, 'profile.lastName') + get(AppointmentVisitorPreOrder, 'profile.firstName') : get(order, 'userName', '未填寫')
      const userPhone = isPubApt.value ? `${get(AppointmentVisitorPreOrder, 'profile.phoneCountryCode')} ${get(AppointmentVisitorPreOrder, 'profile.phoneNumber')}` : get(order, 'userPhone', '未填寫')
      const start = get(order, 'start', null)
      const serviceName = get(order, 'AppointmentService.name', '')
      const subServiceName = get(order, 'AppointmentSubService.name', '')
      const resourceItemName = get(order, 'ResourceItem.name', '-')
      const riskLevel = get(order, 'AppointmentOrder.riskLevel')
      const riskLevelDisplay = getRiskLevelDisplay()

      return {
        numberCode,
        totalPrice,
        userName,
        userPhone,
        start,
        serviceName,
        subServiceName,
        resourceItemName,
        riskLevel,
        riskLevelDisplay,
      }
    })
    return {
      useServiceResource,
      isPubApt,
      displayData,
      statusNotWait,
      shopTimezone,
      displayText,
      showControl,
      checkAction,
    }
  },
  data: () => ({
    showEditDialog: false,
  }),
  computed: {
    isCancel () {
      return getData(this.order, 'AppointmentOrder.status') === 'cancel'
    },
    peopleCount () {
      return getData(this.order, 'AppointmentOrder.peopleCount', 1)
    },
    orersCount () {
      const orders = getData(this.order, 'AppointmentOrder.AppointmentReservations', null)
      if (!orders) return 0
      return orders.length
    },
    attachServices () {
      const services = this.order.AppointmentServiceAttaches
      if (!services) return '-'
      // 已軟刪除（isRemove）的附加服務仍是這筆預約的歷史快照，顯示時加上「已刪除」標記
      const servicesName = map(services, (service) => service.isRemove
        ? `${service.name}${this.$t('orderDetailBlock.attachService.removedSuffix')}`
        : service.name)
      if (!servicesName.length) return '-'
      return servicesName.join('、')
    },
    createdAt () {
      return dateTimeFormat(this.order.createdAt, this.shopTimezone).dateTime
    },
  },
  methods: {
    getData,
    statusContent (status) {
      const statusDict = {
        cancel: '已取消',
        wait: '已預約',
        complete: '完　成',
        absence: '未　到',
      }
      return statusDict[status] || 'N/A'
    },
    dateFormat (date) {
      if (!date) return '無'
      return dateTimeFormat(date, this.shopTimezone).dateTime
    },
    showNotSpecify (order) {
      const notSpecify = order.fromNotSpecify
      if (order.AppointmentUnit) return notSpecify ? `${order.AppointmentUnit?.name} (不指定)` : order.AppointmentUnit?.name
      else return '-'
    },
  },
})
</script>

<style scoped lang="scss">
</style>
